import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

function BarChart(props) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    // Dispose any stale instance bound to this node before creating a new one.
    const existing = echarts.getInstanceByDom(chartDom);
    if (existing) {
      existing.dispose();
    }

    const myChart = echarts.init(chartDom, null, {
      renderer: "canvas",
      useDirtyRect: false,
    });
    chartInstanceRef.current = myChart;

    if (props.response && Object.keys(props.response).length > 0) {
      // Convert the object into an array of user responses
      const usersData = Object.values(props.response);

      const labels = [];
      const seriesData = [];

      usersData.forEach((user) => {
        const email = user.email || user.userEmail || "";
        const displayName = email || "Anonymous";

        let maxPossiblePoints = 0;
        if (user.ques && user.ques.length) {
          maxPossiblePoints = user.ques.reduce(
            (total, question) => total + (Number(question.points) || 0),
            0
          );
        }

        const totalPoints = Number(user.totalPoints) || 0;
        let percentage = 0;

        if (maxPossiblePoints > 0) {
          percentage = Number(((totalPoints / maxPossiblePoints) * 100).toFixed(1));
        } else if (user.percentage !== undefined && user.percentage !== null) {
          percentage = Number(Number(user.percentage).toFixed(1));
        }

        labels.push(displayName);
        seriesData.push({
          value: percentage,
          displayName: displayName,
          email: email,
          totalPoints: totalPoints,
          maxPossiblePoints: maxPossiblePoints,
        });
      });

      const option = {
        grid: {
          left: 12,
          right: 56,
          top: 14,
          bottom: 24,
          containLabel: true,
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
            shadowStyle: {
              color: "rgba(103, 58, 183, 0.06)",
            },
          },
          backgroundColor: "#ffffff",
          borderColor: "#dadce0",
          borderWidth: 1,
          padding: [10, 14],
          textStyle: {
            color: "#202124",
            fontFamily: "Inter, 'Google Sans', Roboto, sans-serif",
          },
          extraCssText: "box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12); border-radius: 8px;",
          formatter: function (params) {
            if (!params || !params.length) return "";
            const item = params[0];
            const dataObj = item.data || {};
            const scoreVal = typeof dataObj === "object" ? dataObj.value : item.value;
            const name = dataObj.displayName || item.name || "Respondent";
            const emailLine =
              dataObj.email && dataObj.email !== name
                ? `<div style="font-size: 11px; color: #5f6368; margin-top: 1px;">${dataObj.email}</div>`
                : "";
            const pointsLine =
              dataObj.maxPossiblePoints > 0
                ? `<div style="margin-top: 8px; font-size: 12px; color: #3c4043; display: flex; justify-content: space-between; gap: 16px;">
                    <span>Points:</span>
                    <strong style="color: #202124;">${dataObj.totalPoints} / ${dataObj.maxPossiblePoints} pts</strong>
                  </div>`
                : "";

            return `
              <div style="min-width: 170px; line-height: 1.4;">
                <div style="font-weight: 700; font-size: 13px; color: #202124;">${name}</div>
                ${emailLine}
                <div style="margin-top: 8px; display: flex; align-items: center; justify-content: space-between; gap: 16px;">
                  <span style="font-size: 12px; color: #5f6368;">Score:</span>
                  <span style="font-weight: 700; font-size: 12px; color: #673ab7; background: #f0ebf8; padding: 2px 8px; border-radius: 10px;">${scoreVal}%</span>
                </div>
                ${pointsLine}
              </div>
            `;
          },
        },
        xAxis: {
          type: "value",
          min: 0,
          max: 100,
          interval: 20,
          axisLabel: {
            formatter: "{value}%",
            color: "#5f6368",
            fontSize: 11,
            fontWeight: 500,
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: "#f1f3f4",
              type: "dashed",
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: "#dadce0",
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: "category",
          data: labels,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            color: "#202124",
            fontSize: 12,
            fontWeight: 500,
            margin: 12,
            formatter: function (val) {
              return val.length > 28 ? val.slice(0, 26) + "…" : val;
            },
          },
        },
        series: [
          {
            name: "Score",
            type: "bar",
            barWidth: 20,
            showBackground: true,
            backgroundStyle: {
              color: "#f0ebf8",
              borderRadius: [0, 10, 10, 0],
            },
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: "#8e24aa" },
                { offset: 1, color: "#673ab7" },
              ]),
              borderRadius: [0, 10, 10, 0],
            },
            label: {
              show: true,
              position: "right",
              distance: 10,
              formatter: "{c}%",
              color: "#673ab7",
              fontWeight: 700,
              fontSize: 12,
            },
            data: seriesData,
          },
        ],
      };

      myChart.setOption(option);
      requestAnimationFrame(() => myChart.resize());
    }

    const resizeObserver = new ResizeObserver(() => {
      if (myChart) {
        myChart.resize();
      }
    });
    resizeObserver.observe(chartDom);

    const handleWindowResize = () => {
      if (myChart) {
        myChart.resize();
      }
    };
    window.addEventListener("resize", handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
      myChart.dispose();
      chartInstanceRef.current = null;
    };
  }, [props.response]);

  const responseCount = props.response ? Object.keys(props.response).length : 0;
  const chartHeight = Math.max(220, Math.min(650, responseCount * 48 + 50));

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height: `${chartHeight}px`,
        marginTop: "8px",
      }}
    />
  );
}

export default BarChart;